import React, { useState } from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IIncident } from "../../../shared/interfaces/incident/IIncident";
import DeleteModal from "../../../shared/components/DeleteModal";
import { IncidentService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import incidentAlertsSlice from "../../../store/slices/incidentAlerts.slice";
import PageSizeSelect from "../../../shared/components/PageSizeSelect";
import Pagination from "../../../shared/components/Pagination";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";

interface IProps {
  incidents: IIncident[];
  pagination: IPagination;
  totalPages: number;
  handleUpdateList: () => void;
  changeFilterHandler: (
    name: string,
    value: string | number | boolean | number[] | null,
  ) => void;
  showActions?: boolean;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case "PENDING":
      return "warning";
    case "IN_PROGRESS":
      return "info";
    case "RESOLVED":
      return "success";
    case "CANCELLED":
      return "secondary";
    default:
      return "primary";
  }
};

const getIncidentTypeBadge = (type: string) => {
  switch (type) {
    case "ACTIVE_SHOOTER":
      return "danger";
    case "FIRE":
      return "warning";
    case "MEDICAL":
      return "info";
    case "NATURAL_DISASTER":
      return "dark";
    case "HAZMAT":
      return "warning";
    case "SECURITY_THREAT":
      return "danger";
    default:
      return "secondary";
  }
};

const IncidentList = ({
  incidents,
  pagination,
  totalPages,
  handleUpdateList,
  changeFilterHandler,
  showActions = true,
}: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<
    IIncident | undefined
  >(undefined);

  const handleCloseDelete = () => {
    setIsDeleteVisible(false);
    setSelectedIncident(undefined);
  };

  const handleDeleteIncident = (id: number) => {
    IncidentService.delete(id)
      .then(() => {
        handleUpdateList();
        // The backend's socket "delete" event fires before the row is gone,
        // so the badge must re-count once the delete has really finished
        dispatch(incidentAlertsSlice.actions.requestCountRefresh());
        handleCloseDelete();
        enqueueSnackbar(
          t("toasts.successfullyDeleted", {
            name: t(`incident.type.${selectedIncident?.incidentType}`),
          }),
          {
            variant: "success",
          },
        );
      })
      .catch(handleError);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  return (
    <React.Fragment>
      <div className="table-responsive">
        <table className="table table-hover" id="pc-dt-simple">
          <thead>
            <tr>
              <th>{t("incident.type.label")}</th>
              <th>{t("incident.status")}</th>
              <th>{t("incident.drillMode")}</th>
              <th>{t("incident.user")}</th>
              <th>{t("incident.beacon")}</th>
              <th>{t("incident.createdAt")}</th>
              {showActions && <th>{t("general.action")}</th>}
            </tr>
          </thead>
          <tbody>
            {incidents.map((incident) => {
              return (
                <tr
                  key={incident?.id}
                  className={
                    incident?.viewed === false ? "incident-row--unviewed" : ""
                  }
                >
                  <td>
                    <Badge bg={getIncidentTypeBadge(incident?.incidentType)}>
                      {t(`incident.type.${incident?.incidentType}`)}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={getStatusBadge(incident?.statusType)}>
                      {t(`incident.statusType.${incident?.statusType}`)}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={incident?.drillMode ? "warning" : "secondary"}>
                      {incident?.drillMode ? "True" : "False"}
                    </Badge>
                  </td>
                  <td>{incident?.userFullName || "-"}</td>
                  <td>{incident?.beaconTitle || "-"}</td>
                  <td>{formatDate(incident?.createdAt)}</td>
                  {showActions && (
                    <td>
                      <Link
                        to={`/incidents/${incident?.id}`}
                        className="avtar avtar-xs btn-link-secondary"
                      >
                        <i className="ti ti-eye f-20"></i>
                      </Link>
                      <Link
                        to="#"
                        onClick={() => {
                          setSelectedIncident(incident);
                          setIsDeleteVisible(true);
                        }}
                        className="avtar avtar-xs btn-link-secondary"
                      >
                        <i className="ti ti-trash f-20"></i>
                      </Link>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PageSizeSelect
        value={pagination.size}
        onChange={(size) => changeFilterHandler("size", size)}
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        onPageChange={(page) => changeFilterHandler("page", page)}
      />

      <DeleteModal
        show={isDeleteVisible}
        handleClose={handleCloseDelete}
        handleDeleteId={() =>
          handleDeleteIncident(Number(selectedIncident?.id))
        }
        name={
          selectedIncident
            ? t(`incident.type.${selectedIncident.incidentType}`)
            : ""
        }
      />
    </React.Fragment>
  );
};

export default IncidentList;
